import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toggleLoginModal, logout } from "../store/slice/authSlice";
import { LogoImg } from "./Logo";
import { User, LogOut, ChevronDown } from "lucide-react";

export default function Header() {
  const dispatch = useDispatch();
  const { isLoggedIn, user } = useSelector((state) => state.auth);

  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  const handleLogout = () => {
    setOpen(false);
    dispatch(logout());
  };

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Close on ESC
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100 shadow-sm">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">

        {/* LOGO */}
        <Link to="/" className="flex items-center gap-2 group">
          <LogoImg className="h-12 w-auto object-contain group-hover:scale-105 transition-transform duration-300" />
        </Link>

        {/* RIGHT SIDE */}
        <div className="flex items-center gap-4">

          {/* CUSTOM DESIGN LINK */}
          <Link
            to="/custom-template-process"
            className="hidden md:block text-orange-600 font-semibold hover:underline"
          >
            Custom Design
          </Link>

          {isLoggedIn ? (
            <div className="relative" ref={dropdownRef}>

              {/* PROFILE BUTTON */}
              <button
                onClick={() => setOpen((v) => !v)}
                className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full hover:bg-yellow-50 border border-transparent hover:border-yellow-200 transition-all"
                aria-haspopup="menu"
                aria-expanded={open}
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center text-white shadow-sm">
                  {user?.name ? (
                    <span className="font-bold text-sm">
                      {user.name.charAt(0).toUpperCase()}
                    </span>
                  ) : (
                    <User className="w-5 h-5" />
                  )}
                </div>

                <span className="text-sm font-semibold text-gray-700 max-w-[120px] truncate">
                  {user?.name || "My Account"}
                </span>

                <ChevronDown
                  className={`w-4 h-4 text-gray-500 transition-transform ${open ? "rotate-180" : ""}`}
                />
              </button>

              {/* DROPDOWN MENU */}
              {open && (
                <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-100 rounded-xl shadow-lg overflow-hidden z-50 animate-fadeIn">

                  <Link
                    to="/generated-invitations"
                    onClick={() => setOpen(false)}
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    My Orders
                  </Link>

                  {/* Future: Profile page
                  <Link
                    to="/profile"
                    onClick={() => setOpen(false)}
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Profile
                  </Link>
                  */}

                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-red-50 flex items-center gap-2"
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            /* LOGIN BUTTON */
            <button
              onClick={() => dispatch(toggleLoginModal(true))}
              className="bg-gradient-to-r from-orange-500 to-yellow-500 text-white px-4 py-2 rounded-full font-medium shadow hover:shadow-lg transition"
            >
              Login / Signup
            </button>
          )}

          {/* MAIN CTA */}
          <Link
            to="/category/hindu-wedding"
            className="hidden sm:block bg-yellow-500 text-white px-5 py-2 rounded-full font-medium hover:bg-yellow-600 transition shadow-md hover:shadow-lg"
          >
            Start Creating →
          </Link>

        </div>
      </div>
    </header>
  );
}